/**
 * Convert AI-written "sendWhen" strings into a canonical schedule offset
 * relative to webinar day (0 = webinar day).
 *
 * Assumption baked into the Make.com prompts: the webinar runs on
 * Thursday of Week 2, so:
 *
 *   Mon Week 1  → -10     Mon Week 2  → -3
 *   Tue Week 1  → -9      Tue Week 2  → -2
 *   Wed Week 1  → -8      Wed Week 2  → -1
 *   Thu Week 1  → -7      Thu Week 2  →  0  (WEBINAR)
 *   Fri Week 1  → -6      Fri Week 2  → +1
 *
 * Post-webinar follow-ups use day-of-week or "Next Week" labels.
 */

export type TimeOfDay = 'morning' | 'midday' | 'evening' | 'before-webinar' | 'at-webinar' | 'post-webinar';

export interface ScheduleSlot {
  /** Integer days offset from webinar day (0 = day of webinar) */
  dayOffset: number;
  /** Optional time of day for sort-within-day and display */
  timeOfDay?: TimeOfDay;
  /** Canonical label, e.g. "Mon · W1", "Thu · Webinar", "Fri · Morning" */
  shortLabel: string;
  /** The raw source string we parsed */
  raw: string;
}

const DAY_INDEX: Record<string, number> = {
  sun: 0, sunday: 0,
  mon: 1, monday: 1,
  tue: 2, tues: 2, tuesday: 2,
  wed: 3, weds: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4,
  fri: 5, friday: 5,
  sat: 6, saturday: 6,
};

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function normalize(input: string): string {
  return input.toLowerCase().replace(/[—–-]+/g, '-').replace(/\s+/g, ' ').trim();
}

function dayNameToIndex(name: string): number | null {
  const key = name.toLowerCase().replace(/\.$/, '');
  return DAY_INDEX[key] ?? null;
}

function timeOfDayFromPhrase(s: string): TimeOfDay | undefined {
  if (/morning/.test(s)) return 'morning';
  if (/afternoon|midday|noon/.test(s)) return 'midday';
  if (/evening|night/.test(s)) return 'evening';
  if (/hour[s]? before|before webinar|before masterclass/.test(s)) return 'before-webinar';
  if (/start|we just started|webinar start|masterclass start|going live|live now/.test(s))
    return 'at-webinar';
  if (/post-?masterclass|post-?webinar|after the (masterclass|webinar)/.test(s)) return 'post-webinar';
  return undefined;
}

/**
 * Parse a sendWhen string into a schedule slot. Returns null if we can't
 * confidently locate it — caller can render the raw label as a fallback.
 */
export function parseSendWhen(
  sendWhen: string | undefined | null,
  opts: { defaultKind?: 'marketing' | 'social' | 'follow-up' } = {},
): ScheduleSlot | null {
  if (!sendWhen) return null;
  const raw = sendWhen;
  const s = normalize(sendWhen);

  const timeOfDay = timeOfDayFromPhrase(s);

  // Special webinar-day markers
  if (/webinar start|masterclass start|we just started|going live|live now/.test(s)) {
    return { dayOffset: 0, timeOfDay: 'at-webinar', shortLabel: 'Thu · Live', raw };
  }
  if (/1\s*hour\s*before|one hour before|hour before/.test(s)) {
    return {
      dayOffset: 0,
      timeOfDay: 'before-webinar',
      shortLabel: 'Thu · 1hr before',
      raw,
    };
  }

  // "DayName [Morning|Night|…] — Week N"
  const weekMatch = s.match(
    /\b(sunday|monday|tuesday|tues|wednesday|weds|thursday|thurs|thu|friday|saturday|mon|tue|wed|thu|fri|sat|sun)\b[^a-z0-9]*?(morning|afternoon|evening|night|midday|noon)?\b[^a-z0-9]*?week\s*(\d)/i,
  );
  if (weekMatch) {
    const dayIdx = dayNameToIndex(weekMatch[1]);
    const week = parseInt(weekMatch[3] ?? '1', 10);
    if (dayIdx != null) {
      // Anchor: Thursday of Week 2 = offset 0 (dayIdx 4)
      const dayOffset = (week - 2) * 7 + (dayIdx - 4);
      const shortLabel = `${DAY_SHORT[dayIdx]} · W${week}${timeOfDay ? ` ${timeOfDay}` : ''}`;
      return { dayOffset, timeOfDay, shortLabel, raw };
    }
  }

  // Post-webinar markers
  if (/4\s*hours?\s*post|hours?\s*post-?masterclass|hours?\s*post-?webinar|thursday\s*(evening|night)\s*post/.test(s)) {
    return { dayOffset: 0, timeOfDay: 'post-webinar', shortLabel: 'Thu · Night', raw };
  }

  // Bare day-of-week (common in follow-ups: "Friday Morning", "Monday Morning")
  const dayMatch = s.match(
    /\b(sunday|monday|tuesday|tues|wednesday|weds|thursday|thurs|thu|friday|saturday|mon|tue|wed|thu|fri|sat|sun)\b/i,
  );
  if (dayMatch) {
    const dayIdx = dayNameToIndex(dayMatch[1]);
    if (dayIdx != null) {
      // Default-kind heuristic: for follow-ups, day-only names are post-webinar
      // (first occurrence AFTER Thursday within the next 7 days).
      const kind = opts.defaultKind ?? 'follow-up';
      const webinarDayIdx = 4; // Thursday
      let diff = (dayIdx - webinarDayIdx + 7) % 7;
      if (diff === 0 && kind === 'follow-up') diff = 7; // "Thursday" in follow-up = a week later
      const dayOffset = diff;
      return {
        dayOffset,
        timeOfDay,
        shortLabel: `${DAY_SHORT[dayIdx]}${timeOfDay ? ` · ${timeOfDay}` : ''}`,
        raw,
      };
    }
  }

  return null;
}

export interface ScheduleEntry<TData> {
  id: string;
  data: TData;
  slot: ScheduleSlot | null;
  /** Pre-parse order (falls back to this if slot is null) */
  order: number;
}

/**
 * Sort schedule entries by (dayOffset, timeOfDay, order) so the calendar
 * column renders in the order items will actually occur.
 */
const TIME_ORDER: Record<TimeOfDay, number> = {
  morning: 0,
  midday: 1,
  evening: 2,
  'before-webinar': 3,
  'at-webinar': 4,
  'post-webinar': 5,
};

export function sortByScheduledOrder<T>(
  entries: ScheduleEntry<T>[],
): ScheduleEntry<T>[] {
  return [...entries].sort((a, b) => {
    const aOff = a.slot?.dayOffset ?? 999;
    const bOff = b.slot?.dayOffset ?? 999;
    if (aOff !== bOff) return aOff - bOff;
    const aTime = a.slot?.timeOfDay ? TIME_ORDER[a.slot.timeOfDay] : 0;
    const bTime = b.slot?.timeOfDay ? TIME_ORDER[b.slot.timeOfDay] : 0;
    if (aTime !== bTime) return aTime - bTime;
    return a.order - b.order;
  });
}
